import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const WhatIfBody = withListOverflow(WhatIfListOwner);
// src/features/plan/WhatIfPane.jsx
//
// PLAN-WHATIF-S1 — the "Plan | What-If" segmented lens for the Market Plan
// page. Read-only: it never mutates plan data, never calls a save/update
// client, and never renders an editable market row. See marketsService.js
// (computeWhatIfMarketScenario, WHATIF_* constants) for the scenario math.
//
// Persistence: the last-used lever set is saved to localStorage so the
// scenario survives navigation/reload; the toggle position itself is NOT
// persisted here (MarketPlanPage owns that so it can also honor the
// `?whatif=1` deep link Pulse's SpaceHeadroomPane uses).
//
// PLAN-WHATIF-REDESIGN-S1 — the top zone now renders the same 5-tile KPI
// strip Plan mode uses (see KPI_TILE_DEFS/computeWhatIfPortfolioKpis in
// marketsService.js), each tile split into Today/Scenario blocks with a
// right-aligned delta. The body is a 2/3 (today-vs-scenario compare panel) +
// 1/3 (scenario controls stacked over a templated result pane) grid. Lever
// state + rows/portfolio aggregates are exposed via useWhatIfScenario() so
// MarketPlanPage can render the KPI strip and the body from the same source
// of truth.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { OUTBOUND_LINK_STYLE } from "../../v2/pulse/pulseConstants.js";
import { StageBadge } from "../../components/StageBadge.jsx";
import { PlanTabBar } from "./PlanTabBar.jsx";
import {
  WHATIF_HORIZON_OPTIONS,
  WHATIF_GUARDRAILS,
  WHATIF_DEFAULT_LEVERS,
  WHATIF_NEUTRAL_LEVERS,
  computeWhatIfMarketScenario,
  computeWhatIfPortfolioKpis,
  isWhatIfActiveMarket,
  computeBlendedNetGrowthRate,
  parseTimelineDate,
  computeOfficeHorizonHeadroom,
  isMarketMember,
  isCountedMarketOffice,
  computeAnnualCostBreakdown,
} from "./marketsService.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiTileRow, KPI_TILE_ROW_HEIGHT } from "./KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { CondensedKpiMetric } from "../../components/CondensedKpiMetric.jsx";
import { KpiBandPresentationContext } from "../../contexts/KpiBandContext.jsx";
import { useContext } from "react";
import { SCENARIO_FILL_COLOR, SCENARIO_MARKER_COLOR } from "./ScenarioViewKey.jsx";
import { fmtKpiUsd } from "../../utils/fmtAbbreviated.js";
import { SharingRatioValue } from "../../components/SharingRatioValue.jsx";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";

const WHATIF_LEVERS_STORAGE_KEY = "ripple.plan.whatif.levers.v2";

// WHATIF-BUILD-ONLINE-INDICATOR-S1: UTC-safe month/year formatter.
// Never uses toLocaleString — avoids locale drift in tests and CI.
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatMonthYear(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  return `${MONTH_NAMES[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

// ─── Pure helpers for the inline expand pane (MARKETPLAN-ROW-EXPAND-S1) ──────
//
// Both functions are exported so they can be tested in isolation without
// mounting any React component. They read only from the data already present
// on market.locations / the scenario object — no new server round-trip.

// Stages that indicate an in-build office (mirrors marketsService.js IN_BUILD_STAGES)
const EXPAND_IN_BUILD_STAGES = new Set(["build"]);

/**
 * One-sentence DRIVER summary for a market's expand pane.
 *
 * Describes today's capacity state and lists every build / exit event
 * within the current horizon, each with the capacity transition it triggers.
 *
 * @param {ReturnType<typeof computeWhatIfMarketScenario>} scenario
 * @returns {string}
 */
export function buildMarketDriverSentence(scenario) {
  const todayPct = scenario.assignPctToday;
  let intro;
  if (!Number.isFinite(todayPct) || todayPct > 100) {
    intro = "Over capacity today";
  } else if (todayPct >= 90) {
    intro = "Tight on capacity today";
  } else {
    intro = "Comfortable today";
  }

  const parts = [];
  for (const ev of (scenario.buildEvents ?? [])) {
    parts.push(
      `+${ev.seatsAdded} ${ev.officeName} build (online ${formatMonthYear(ev.onlineDate)}) lifts capacity ${ev.capacityBefore} \u2192 ${ev.capacityAfter}`
    );
  }
  for (const ev of (scenario.exitEvents ?? [])) {
    parts.push(
      `\u2212${ev.seatsRemoved} ${ev.officeName} closes ${formatMonthYear(ev.closeDate)}, drops capacity ${ev.capacityBefore} \u2192 ${ev.capacityAfter}`
    );
  }

  if (parts.length === 0) return `${intro}.`;
  return `${intro}; ${parts.join("; ")}.`;
}

/**
 * Derive one display row per active office for the expand pane table.
 *
 * Reads futureScheduleRows (already fetched by /api/plan via the same
 * scheduleCapacityAtHorizon / futureScheduleRows path used by What-If
 * horizon-capacity math) for the "next scheduled change" column.
 *
 * @param {object[]} locations  market.locations (camelCase)
 * @param {Date}     [today]    Defaults to new Date(); pass for determinism.
 * @returns {Array<{
 *   id: string, name: string, status: string, plan: string,
 *   nextEvent: {label: string, months: number}|null,
 *   people: number|null, seats: number,
 *   nextSchedule: {dateStr: string, seats: number, delta: number}|null,
 *   headroom: number|null,
 * }>}
 */
export function buildMarketOfficeRows(locations, today = new Date()) {
  // Browse membership is wider than the effective office headline: Draft
  // stays visible but contributes no committed supply or current occupancy.
  return (locations ?? [])
    .filter(isMarketMember)
    .map((loc) => {
      const stage = loc.stage ?? "";
      const isInBuild = EXPAND_IN_BUILD_STAGES.has(stage);
      const isDraft = stage === "draft";
      const statusLabel = isDraft ? "Draft" : stage === "vacated" ? "Vacated" : (isInBuild ? "In-build" : "Active");
      // In-build offices carry no disposition — mirrors the existing
      // in-build-carries-no-disposition rule elsewhere (headroom/capacity):
      // never surface a raw disposition value (e.g. "expand") for them.
      const planLabel = isDraft ? "—" : isInBuild ? "In-Build" : (loc.disposition ? loc.disposition.charAt(0).toUpperCase() + loc.disposition.slice(1).toLowerCase() : "—");

      // Next lease event: prefer nextDeadline (critical date), fall back to leaseExpiry
      let nextEvent = null;
      const dl = loc.nextDeadline;
      if (dl?.dueDate) {
        const d = parseTimelineDate(dl.dueDate);
        if (d && d > today) {
          const months = Math.round((d - today) / (1000 * 60 * 60 * 24 * 30.5));
          nextEvent = { label: dl.label ?? "Decision", months };
        }
      }
      if (!nextEvent && loc.leaseExpiry) {
        const d = parseTimelineDate(loc.leaseExpiry);
        if (d && d > today) {
          const months = Math.round((d - today) / (1000 * 60 * 60 * 24 * 30.5));
          nextEvent = { label: "Lease expiry", months };
        }
      }

      // People count + installed seats
      const people = loc.headcount ?? null;
      const seats = typeof loc.installedCapacity === "number"
        ? loc.installedCapacity
        : (isInBuild && typeof loc.plannedSeats === "number" ? loc.plannedSeats : 0);

      // Next scheduled capacity change — first future row from location_capacity_schedule
      // (sorted ascending by effectiveDate, already done server-side)
      let nextSchedule = null;
      for (const r of (!isDraft && Array.isArray(loc.futureScheduleRows) ? loc.futureScheduleRows : [])) {
        const d = parseTimelineDate(r.effectiveDate);
        if (d && d > today) {
          const installedNow = typeof loc.installedCapacity === "number" ? loc.installedCapacity : 0;
          nextSchedule = {
            dateStr: String(r.effectiveDate).slice(0, 7),
            seats: r.installedSeats,
            delta: r.installedSeats - installedNow,
          };
          break;
        }
      }

      // RETIRE-LEGACY-FLAT-SEATS-ARTIFACT: headroom is a today-only densification
      // metric. In-build offices have nothing installed today, so there is
      // nothing to densify — return null (shown as "—"). For active offices,
      // call the schedule-aware service so Bankside-style phantom +100
      // (maxCapacity − 0 when nothing is installed yet) never surfaces here.
      const headroom = isDraft || isInBuild ? null : computeOfficeHorizonHeadroom(loc, today);

      return { id: loc.id, name: loc.locationAlias ?? loc.id, stage: loc.stage ?? "", status: statusLabel, plan: planLabel, nextEvent, people: isDraft ? 0 : people, seats: isDraft ? 0 : seats, nextSchedule, headroom };
    });
}

// WHATIF-BUILD-ONLINE-INDICATOR-S1: alias T tokens for readability in this file.
const BUILD_BLUE  = T.whatifBuildBlue;   // blue-600, hatch stroke + post-build line
const HATCH_WHITE = T.whatifBuildHatch;  // blue-100, hatch gap stripe

// WHATIF-EXIT-SEGMENT-S1: grey used for the "office closing" flag text below
// the Scenario bar (the hatch background this used to also color was removed
// by MARKETPLAN-COMPARE-LEGEND-CLEANUP-S1).
const EXIT_GRAY  = T.borderDark;   // #cbd5e1 slate-300

function loadStoredLevers() {
  try {
    const raw = window.localStorage.getItem(WHATIF_LEVERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return { ...WHATIF_DEFAULT_LEVERS, ...parsed };
  } catch {
    return null;
  }
}

function saveStoredLevers(levers) {
  try {
    window.localStorage.setItem(WHATIF_LEVERS_STORAGE_KEY, JSON.stringify(levers));
  } catch {
    // Storage unavailable (private mode, quota, etc.) — scenario just won't persist.
  }
}

// ─── useWhatIfScenario — lever state + persistence + derived rows/portfolio ──
//
// MARKET-PLAN-PURGE-TESTS-AND-RECONCILE-COUNT-S1: `markets` continues to
// drive `rows` (the compare table + verdict banner), which intentionally
// respects Plan's search/strategy/region/status/flags/hub toolbar filters —
// that filtering behavior is unchanged. `kpiMarkets` is a second, optional
// list used ONLY for the top KPI strip's `portfolio` aggregate; the caller
// passes the canonical active-market set (selectActiveMarketsForKpis) so the
// Markets/Peak util/Over-capacity/Portfolio balance tiles stay in lockstep
// with Plan and Timeline regardless of the toolbar's current filter/search
// state. Defaults to `markets` when omitted, preserving prior behavior.
//
// SCENARIO-SEED-FROM-POLICY: `policyBaseline` (optional) is the resolved
// office policy row from GET /api/plan/policy. When provided:
//   • Levers are seeded from policy on first mount if no stored session exists.
//   • "Reset to today" resets all policy-backed levers to policyBaseline values
//     so every Δ badge reads zero (Scenario == Policy).
//   • Guardrail values (densityFloorSfPerSeat, targetBufferPct) come from policy.
// This hook never writes to the policy endpoint; it is strictly read-only.

export function useWhatIfScenario(markets, kpiMarkets, policyBaseline, { persist = true } = {}) {
  // Record-scoped scenarios (persist=false) start fresh and never read or
  // write the portfolio session, so one market's levers cannot leak.
  const [levers, setLevers] = useState(() => (persist ? loadStoredLevers() : null) ?? WHATIF_DEFAULT_LEVERS);

  useEffect(() => {
    if (persist) saveStoredLevers(levers);
  }, [levers, persist]);

  // SCENARIO-SEED-FROM-POLICY: seed policy-backed levers from the baseline when
  // it first arrives — only when there is no in-progress stored session.
  // Once seeded, the flag stays true so page navigations don't re-seed and
  // overwrite deliberate user changes.
  const policySeeded = useRef(false);
  useEffect(() => {
    if (!policyBaseline || policySeeded.current) return;
    policySeeded.current = true;
    const stored = persist ? loadStoredLevers() : null;
    if (!stored) {
      setLevers((prev) => ({
        ...prev,
        rtoDaysPerMonth:   policyBaseline.rtoDaysPerMonth   ?? WHATIF_DEFAULT_LEVERS.rtoDaysPerMonth,
        sharingRatio:      policyBaseline.sharingRatio      ?? WHATIF_DEFAULT_LEVERS.sharingRatio,
        hotelingHoldPct:   policyBaseline.hotelingHoldPct   ?? WHATIF_DEFAULT_LEVERS.hotelingHoldPct,
        confGivebackPct:   policyBaseline.confGivebackPct   ?? WHATIF_DEFAULT_LEVERS.confGivebackPct,
        teamBlockWastePct: policyBaseline.teamBlockWastePct ?? WHATIF_DEFAULT_LEVERS.teamBlockWastePct,
      }));
    }
  }, [policyBaseline]);

  // Guardrails come from the live policy when available; fall back to the
  // compiled-in constants so the scenario math is never undefined.
  const guardrails = useMemo(() => policyBaseline
    ? { densityFloorSfPerSeat: policyBaseline.densityFloorSfPerSeat, targetBufferPct: policyBaseline.targetBufferPct }
    : WHATIF_GUARDRAILS,
  [policyBaseline]);

  const rows = useMemo(() => (
    (markets ?? []).map((market) => ({ market, scenario: computeWhatIfMarketScenario(market, levers, guardrails) }))
  ), [markets, levers, guardrails]);

  const kpiRows = useMemo(() => {
    const source = kpiMarkets ?? markets;
    return (source ?? []).map((market) => ({ market, scenario: computeWhatIfMarketScenario(market, levers, guardrails) }));
  }, [kpiMarkets, markets, levers, guardrails]);

  const portfolio = useMemo(() => computeWhatIfPortfolioKpis(kpiRows), [kpiRows]);

  return {
    levers,
    setLevers,
    // WHATIF-NETGROWTH-PERMARKET-S1 / SCENARIO-SEED-FROM-POLICY:
    // "Reset to today" resets policy-backed levers to policyBaseline so all Δ
    // badges read zero (Scenario == Policy), clears every pinned per-market
    // net-growth override, and leaves netGrowthDefault at 0.
    // Falls back to WHATIF_NEUTRAL_LEVERS values when policy is not yet loaded.
    resetLevers: () => {
      const base = policyBaseline ?? WHATIF_NEUTRAL_LEVERS;
      setLevers({
        ...WHATIF_NEUTRAL_LEVERS,
        horizonMonths:     WHATIF_HORIZON_OPTIONS[0].months,
        netGrowthByMarket: {},
        rtoDaysPerMonth:   base.rtoDaysPerMonth   ?? WHATIF_NEUTRAL_LEVERS.rtoDaysPerMonth,
        sharingRatio:      base.sharingRatio      ?? WHATIF_NEUTRAL_LEVERS.sharingRatio,
        hotelingHoldPct:   base.hotelingHoldPct   ?? WHATIF_NEUTRAL_LEVERS.hotelingHoldPct,
        confGivebackPct:   base.confGivebackPct   ?? WHATIF_NEUTRAL_LEVERS.confGivebackPct,
        teamBlockWastePct: base.teamBlockWastePct ?? WHATIF_NEUTRAL_LEVERS.teamBlockWastePct,
      });
    },
    rows,
    portfolio,
    guardrails,
  };
}

// ─── Plan sub-nav tab bar (MARKETPLAN-SWITCHER-OPT5-TABS-A) ───────────────────

const PLAN_WHATIF_TABS = [
  { key: "plan",            label: "Plan",     desc: "set dispositions", testid: "plan-whatif-toggle-plan"            },
  { key: "market_pressure", label: "Pressure", desc: "capacity runway",  testid: "plan-whatif-toggle-market-pressure" },
  { key: "whatif",          label: "Scenario", desc: "model changes",    testid: "plan-whatif-toggle-whatif"          },
  { key: "recommendations", label: "Guidance", desc: "recommendations",  testid: "plan-whatif-toggle-recommendations" },
];

export function PlanWhatIfToggle({ view, onChange }) {
  return (
    <PlanTabBar
      tabs={PLAN_WHATIF_TABS}
      value={view}
      onChange={onChange}
      ariaLabel="Scenario view"
    />
  );
}

// ─── Shared KPI strip — Today/Scenario split tiles (PLAN-WHATIF-REDESIGN-S1) ─
// Grid sizing matches Plan mode's KpiStrip (MarketPlanPage.jsx): 5 columns,
// minmax(140px, 260px) each, 12px gap — same visual rhythm in both modes.

function toneColor(tone) {
  switch (tone) {
    case "bad":  return T.ragRed;
    case "warn": return T.ragAmber;
    case "good": return T.greenDeep;
    default:     return T.light;
  }
}

function formatSigned(n) {
  const r = Math.round(n);
  return `${r >= 0 ? "+" : ""}${r}`;
}

// MARKET-PLAN-CROSSVIEW-CONSISTENCY-S1: height is fixed to KPI_TILE_ROW_HEIGHT
// (structurally, not by eyeballed padding) so a What-If tile — solo or split
// — always occupies exactly the same box as a Plan/Timeline single-value
// KpiTile. boxSizing: border-box means the padding is carved out of that
// fixed height rather than adding to it.
function tileShellStyle() {
  return {
    background:    T.white,
    border:        `1px solid ${T.border}`,
    borderRadius:  14,
    padding:       "8px 14px",
    display:       "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height:        KPI_TILE_ROW_HEIGHT,
    boxSizing:     "border-box",
  };
}

function TileLabel({ children }) {
  return (
    <div style={{
      height: 36,
      overflow: "hidden",
      display: "-webkit-box",
      WebkitBoxOrient: "vertical",
      WebkitLineClamp: 2,
      fontSize: T.fsXxs5,
      fontWeight: T.fwBold,
      textTransform: "uppercase",
      letterSpacing: ".03em",
      color: T.light,
      lineHeight: 1.45,
    }}>
      {children}
    </div>
  );
}

function WhatIfSoloTile({ testId, label, value }) {
  const condensed = useContext(KpiBandPresentationContext);
  if (condensed) return <CondensedKpiMetric testId={testId} label={label} value={value} secondary="no change" />;
  return (
    <div data-testid={testId} style={tileShellStyle()}>
      <TileLabel>{label}</TileLabel>
      <div style={{ fontSize: T.fsXl, fontWeight: T.fwBold, color: T.dark }}>{value}</div>
      <div data-testid={`${testId}-delta`} style={{ textAlign: "right", fontSize: T.fsXs, fontWeight: T.fwBold, marginTop: 3, color: T.light }}>
        no change
      </div>
    </div>
  );
}

// WHATIF-KPI-TILES-NEUTRAL-OPTION-A: inline "Today → Scenario" layout with
// muted today value, bold dark scenario value, and amber-outline "scenario"
// tag. Delta is the only colored element — no filled blue/amber tile blocks.
// valueColor: optional override for the today-value and scenario-value number
// color. When provided, both numeric values render in this color instead of
// the defaults (T.light for today, T.dark for scenario).
function WhatIfSplitTile({ testId, label, todayValue, scenarioValue, deltaText, tone, valueColor }) {
  const condensed = useContext(KpiBandPresentationContext);
  if (condensed) return <CondensedKpiMetric testId={testId} label={label}
    value={<>Today {todayValue} → Scenario {scenarioValue}</>} secondary={deltaText} color={valueColor} />;
  return (
    <div data-testid={testId} style={tileShellStyle()}>
      <TileLabel>{label}</TileLabel>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "nowrap", overflow: "hidden" }}>
        <span style={{ fontSize: T.fsMd5, fontWeight: T.fwBold, color: valueColor ?? T.light, flexShrink: 0 }}>{todayValue}</span>
        <span style={{ color: T.border, fontSize: T.fsBase, flexShrink: 0 }}>→</span>
        <span style={{ fontSize: T.fsLgX, fontWeight: T.fwBold, color: valueColor ?? T.dark, flexShrink: 0 }}>{scenarioValue}</span>
        <span style={{
          fontSize:      T.fsXxxs,
          fontWeight:    T.fwBold,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color:         T.ragAmberText,
          border:        `1px solid ${T.ragAmberBorder}`,
          borderRadius:  5,
          padding:       "0 4px",
          marginLeft:    2,
          lineHeight:    1.5,
          flexShrink:    0,
          whiteSpace:    "nowrap",
        }}>scenario</span>
      </div>
      <div data-testid={`${testId}-delta`} style={{ textAlign: "right", fontSize: T.fsXxs, fontWeight: T.fwBold, color: toneColor(tone) }}>
        {deltaText}
      </div>
    </div>
  );
}

export function WhatIfKpiStrip({ portfolio, allMarkets, globalBand = false }) {
  const Row = globalBand ? KpiBand : KpiTileRow;
  const { canSee } = useSensitivityPolicy();
  const canSeeRentCost = canSee("market_plan.rent_cost");
  // Derive the same cost breakdown shown by Plan mode's Annual Cost tile,
  // synchronously from the allMarkets data already loaded in MarketPlanPage.
  // This eliminates the "—" flash that occurred when the component independently
  // fetched getPortfolioCostSummary() on mount before plan data had arrived.
  const { totalAnnualCost, annualCostBreakdown } = useMemo(() => {
    // What-If forecasting retains its contractual basis; only Plan headlines
    // consume recorded net recovery income.
    const { rentTotal, opexTotal } = computeAnnualCostBreakdown((allMarkets ?? []).map((market) => ({
      ...market, locations: (market.locations ?? []).map((loc) => ({
        ...loc,
        annualRentUsd: loc.contractualAnnualRentUsd !== undefined ? loc.contractualAnnualRentUsd : loc.annualRentUsd,
        annualCost: loc.contractualAnnualCost !== undefined ? loc.contractualAnnualCost : loc.annualCost,
      })),
    })));
    const total = (rentTotal != null || opexTotal != null)
      ? (rentTotal ?? 0) + (opexTotal ?? 0)
      : null;
    const rows = [];
    if (rentTotal != null && rentTotal > 0) {
      rows.push({ label: "Base rent", value: fmtKpiUsd(rentTotal) });
    }
    if (opexTotal != null && opexTotal > 0) {
      rows.push({ label: "Opex (workplace + security)", value: fmtKpiUsd(opexTotal) });
    }
    return { totalAnnualCost: total, annualCostBreakdown: rows.length > 0 ? rows : undefined };
  }, [allMarkets]);

  const seatsDelta   = portfolio.seatsNeededScenario - portfolio.seatsNeededToday;
  const utilDelta    = portfolio.peakUtilScenario    - portfolio.peakUtilToday;
  const overCapDelta = portfolio.overCapacityScenario - portfolio.overCapacityToday;
  const balanceDelta = portfolio.balanceScenario     - portfolio.balanceToday;

  const seatsTone   = seatsDelta > 0 ? "warn" : seatsDelta < 0 ? "good" : "flat";
  const utilTone    = utilDelta > 0 ? "warn" : utilDelta < 0 ? "good" : "flat";
  const overCapTone = overCapDelta > 0 ? "bad" : overCapDelta < 0 ? "good" : "flat";
  const balanceTone = balanceDelta < 0 ? "bad" : balanceDelta > 0 ? "good" : "flat";

  const balanceDeltaText = balanceDelta === 0
    ? "no change"
    : (portfolio.balanceScenario < 0 && portfolio.balanceToday >= 0
        ? "▼ deficit vs today"
        : `${balanceDelta > 0 ? "▲" : "▼"} ${formatSigned(balanceDelta)} vs today`);

  return (
    <div>
      <Row testId="whatif-kpi-strip">
      <WhatIfSoloTile testId="kpi-tile-markets" label="Markets" value={String(portfolio.marketsCount)} />
      {canSeeRentCost && <KpiTile
        testId="kpi-tile-annual-cost"
        label="Annual cost"
        value={totalAnnualCost != null ? fmtKpiUsd(totalAnnualCost) : "—"}
        sub="Rent + opex · across portfolio"
        height={KPI_TILE_ROW_HEIGHT}
        breakdown={annualCostBreakdown}
        color="var(--v2-green)"
      />}
      <WhatIfSplitTile
        testId="kpi-tile-seats-needed"
        label="Seats needed"
        todayValue={Math.round(portfolio.seatsNeededToday).toLocaleString()}
        scenarioValue={Math.round(portfolio.seatsNeededScenario).toLocaleString()}
        deltaText={seatsDelta === 0 ? "no change" : `${seatsDelta > 0 ? "▲" : "▼"} ${formatSigned(seatsDelta)} vs today`}
        tone={seatsTone}
      />
      <WhatIfSplitTile
        testId="kpi-tile-assignment-ratio"
        label="Assignment ratio"
        todayValue={`${Math.round(portfolio.peakUtilToday)}%`}
        scenarioValue={`${Math.round(portfolio.peakUtilScenario)}%`}
        deltaText={utilDelta === 0 ? "no change" : `${utilDelta > 0 ? "▲" : "▼"} ${formatSigned(utilDelta)} pts vs today`}
        tone={utilTone}
        valueColor={portfolio.peakUtilToday > 100 ? T.ragRed : portfolio.peakUtilToday >= 90 ? T.ragAmber : "var(--v2-green)"}
      />
      <WhatIfSplitTile
        testId="kpi-tile-over-capacity"
        label="Markets over capacity"
        todayValue={String(portfolio.overCapacityToday)}
        scenarioValue={String(portfolio.overCapacityScenario)}
        deltaText={overCapDelta === 0 ? "no change" : `${overCapDelta > 0 ? "▲" : "▼"} ${formatSigned(overCapDelta)} vs today`}
        tone={overCapTone}
      />
      <WhatIfSplitTile
        testId="kpi-tile-portfolio-balance"
        label="Portfolio balance"
        todayValue={formatSigned(portfolio.balanceToday)}
        scenarioValue={formatSigned(portfolio.balanceScenario)}
        deltaText={balanceDeltaText}
        tone={balanceTone}
      />
    </Row>
    </div>
  );
}

// ─── Stepper — shared small +/- control for numeric levers ───────────────────

function Stepper({ testId, label, value, displayValue, sub, onChange, step = 1, min, max, negative = false }) {
  const clamp = (v) => {
    let next = v;
    if (min !== undefined) next = Math.max(min, next);
    if (max !== undefined) next = Math.min(max, next);
    return next;
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: T.fsXxs5, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", color: T.light }}>
        {label}
      </span>
      <div style={{ display: "inline-flex", alignItems: "center", border: `1px solid ${T.border}`, borderRadius: 7, overflow: "hidden" }}>
        <button
          type="button"
          data-testid={`${testId}-dec`}
          onClick={() => onChange(clamp(Math.round((value - step) * 1000) / 1000))}
          style={{ width: 26, height: 28, border: "none", background: T.lightGray, color: T.mid, fontSize: T.fsBase, cursor: "pointer" }}
        >
          –
        </button>
        <span
          data-testid={`${testId}-value`}
          style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: negative ? T.ragRed : T.dark, padding: "0 10px", minWidth: 52, textAlign: "center", whiteSpace: "nowrap" }}
        >
          {displayValue}
        </span>
        <button
          type="button"
          data-testid={`${testId}-inc`}
          onClick={() => onChange(clamp(Math.round((value + step) * 1000) / 1000))}
          style={{ width: 26, height: 28, border: "none", background: T.lightGray, color: T.mid, fontSize: T.fsBase, cursor: "pointer" }}
        >
          +
        </button>
      </div>
      {sub && <span style={{ fontSize: T.fsXxs5, color: T.light }}>{sub}</span>}
    </div>
  );
}

// ─── Guardrail chip — fixed, non-editable ────────────────────────────────────

function GuardrailChip({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        display:       "flex",
        flexDirection: "column",
        gap:           2,
        border:        `1px dashed ${T.borderDark}`,
        borderRadius:  8,
        padding:       "6px 11px",
      }}
    >
      <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.4, textTransform: "uppercase", color: T.light }}>
        {label}
      </span>
      <span style={{ fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
        {value}
      </span>
    </div>
  );
}

// ─── WhatIfLeversBar — 5 primary comp levers first, legacy levers + guardrails below ─

function horizonLabel(months) {
  return WHATIF_HORIZON_OPTIONS.find((h) => h.months === months)?.label ?? `${months} mo`;
}

// WHATIF-CONTROLS-STEPPER-ALIGN-S1: every Scenario-control row reserves this
// same fixed-width slot after its control. Net growth renders its real
// ▼/▲ expand toggle here; every other row renders an invisible placeholder
// of identical width so all Steppers line up on the same left edge/column.
const CHEVRON_SLOT_WIDTH = 16;
// Fixed width for the [Stepper + chevron] column so all lever rows align
// regardless of whether a PolicyDeltaBadge is present.
// 26 (–) + 52 (value min-width) + 26 (+) + ~2 (borders) ≈ 106 px stepper
// + 8 gap + 16 chevron = 130 px total.
const STEPPER_CHEVRON_COL_WIDTH = 130;

function ChevronSlotPlaceholder() {
  return (
    <span
      aria-hidden="true"
      style={{ display: "inline-block", width: CHEVRON_SLOT_WIDTH, textAlign: "center", visibility: "hidden", fontSize: T.fsSm }}
    >
      ▼
    </span>
  );
}

// ─── Net growth lever — headcount-weighted per-market overrides ─────────────
//
// WHATIF-NETGROWTH-PERMARKET-S1: collapsed, this row always shows the
// headcount-weighted BLEND of every active market's effective rate (never a
// directly-set number) plus a "weighted"/"N markets set" indicator once ≥1
// market is pinned. The collapsed +/- stepper still only nudges the shared
// default — pinned markets are untouched by it. Expanding reveals the
// default stepper plus one row per active market so a planner can pin an
// individual market's growth rate; "Clear overrides" un-pins everything.
function NetGrowthLever({ levers, onChange, activeMarkets, blendedNetGrowthRate, pinnedMarketCount }) {
  const [expanded, setExpanded] = useState(false);

  const setDefault    = (v) => onChange({ ...levers, netGrowthDefault: v });
  const setMarketRate = (marketId, v) => onChange({
    ...levers,
    netGrowthByMarket: { ...(levers.netGrowthByMarket ?? {}), [marketId]: v },
  });
  const clearOverrides = () => onChange({ ...levers, netGrowthByMarket: {} });

  const rowStyle  = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderTop: `1px solid ${T.lightGray}` };
  const nameStyle = { fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.mid };

  const roundedBlend = Math.round(blendedNetGrowthRate * 10) / 10;

  return (
    <div data-testid="whatif-growth-lever">
      <div style={rowStyle}>
        <span style={{ ...nameStyle, display: "flex", alignItems: "center", gap: 6 }}>
          Net growth
          {pinnedMarketCount > 0 && (
            <span
              data-testid="whatif-growth-weighted-tag"
              style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.3, textTransform: "uppercase", color: T.light, background: T.lightGray, borderRadius: 4, padding: "1px 5px" }}
            >
              weighted
            </span>
          )}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {pinnedMarketCount > 0 && (
            <span data-testid="whatif-growth-pinned-badge" style={{ fontSize: T.fsXxs5, fontWeight: T.fwSemibold, color: T.blue }}>
              {pinnedMarketCount} market{pinnedMarketCount === 1 ? "" : "s"} set
            </span>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: STEPPER_CHEVRON_COL_WIDTH, flexShrink: 0, justifyContent: "flex-end" }}>
            <Stepper
              testId="whatif-growth"
              label=""
              value={levers.netGrowthDefault}
              displayValue={`${roundedBlend >= 0 ? "+" : ""}${roundedBlend}%`}
              onChange={setDefault}
              step={1}
              min={-20}
              max={40}
              negative={roundedBlend < 0}
            />
            <span
              role="button"
              tabIndex={0}
              data-testid="whatif-growth-expand-toggle"
              aria-label={expanded ? "Collapse net growth by market" : "Expand net growth by market"}
              onClick={() => setExpanded((e) => !e)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setExpanded((x) => !x); }}
              style={{ display: "inline-block", width: CHEVRON_SLOT_WIDTH, textAlign: "center", fontSize: T.fsSm, color: T.blue, cursor: "pointer", userSelect: "none" }}
            >
              {expanded ? "▲" : "▼"}
            </span>
          </div>
        </div>
      </div>

      {expanded && (
        <div data-testid="whatif-growth-editor" style={{ padding: "8px 0 4px", borderTop: `1px dashed ${T.lightGray}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.4, textTransform: "uppercase", color: T.light }}>
              Default (unpinned markets)
            </span>
            <Stepper
              testId="whatif-growth-default"
              label=""
              value={levers.netGrowthDefault}
              displayValue={`${levers.netGrowthDefault >= 0 ? "+" : ""}${levers.netGrowthDefault}%`}
              onChange={setDefault}
              step={1}
              min={-20}
              max={40}
              negative={levers.netGrowthDefault < 0}
            />
          </div>

          {activeMarkets.map((market) => {
            const overrides = levers.netGrowthByMarket ?? {};
            const pinned    = Object.prototype.hasOwnProperty.call(overrides, market.id);
            const rate      = pinned ? overrides[market.id] : levers.netGrowthDefault;
            const headcount = market.headcount ?? market.totalHeadcount ?? 0;
            return (
              <div
                key={market.id}
                data-testid={`whatif-growth-market-row-${market.id}`}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderTop: `1px solid ${T.lightGray}` }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                  <span
                    data-testid={`whatif-growth-market-dot-${market.id}`}
                    title={pinned ? "Pinned - follows its own rate" : "Follows the default rate"}
                    style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: pinned ? T.blue : T.border }}
                  />
                  <span style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {market.name}
                  </span>
                  <span style={{ fontSize: T.fsXxs5, color: T.light, whiteSpace: "nowrap" }}>
                    {headcount.toLocaleString()} hc
                  </span>
                </div>
                <Stepper
                  testId={`whatif-growth-market-${market.id}`}
                  label=""
                  value={rate}
                  displayValue={`${rate >= 0 ? "+" : ""}${rate}%`}
                  onChange={(v) => setMarketRate(market.id, v)}
                  step={1}
                  min={-20}
                  max={40}
                  negative={rate < 0}
                />
              </div>
            );
          })}

          <span
            role="button"
            tabIndex={0}
            data-testid="whatif-growth-clear-overrides"
            onClick={clearOverrides}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") clearOverrides(); }}
            style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.blue, cursor: "pointer", display: "inline-block", marginTop: 8 }}
          >
            Clear overrides
          </span>
        </div>
      )}
    </div>
  );
}

// SCENARIO-SEED-FROM-POLICY: small amber Δ badge shown next to a stepper
// when the current lever value differs from the policy baseline.
// testId follows the pattern `whatif-{lever}-policy-delta`.
function PolicyDeltaBadge({ testId, delta, unit = "" }) {
  if (Math.abs(delta) < 0.0001) return null;
  const sign = delta > 0 ? "+" : "";
  const displayDelta = Number.isInteger(delta)
    ? `${sign}${delta}${unit}`
    : `${sign}${(Math.round(delta * 10) / 10).toFixed(1)}${unit}`;
  return (
    <span
      data-testid={testId}
      style={{
        fontSize:      T.fsXxs,
        fontWeight:    T.fwBold,
        color:         T.ragAmberText,
        background:    T.ragAmberBg,
        border:        `1px solid ${T.ragAmberBorder}`,
        borderRadius:  4,
        padding:       "1px 5px",
        whiteSpace:    "nowrap",
        flexShrink:    0,
      }}
    >
      Δ {displayDelta}
    </span>
  );
}

function WhatIfLeversBar({ levers, onChange, onReset, activeMarkets, blendedNetGrowthRate, pinnedMarketCount, policyBaseline }) {
  const set = (patch) => onChange({ ...levers, ...patch });

  const rowStyle  = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderTop: `1px solid ${T.lightGray}` };
  const nameStyle = { fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.mid };

  const pb = policyBaseline;

  return (
    <div
      data-testid="whatif-levers-bar"
      style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px", display: "flex", flexDirection: "column" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.6, textTransform: "uppercase", color: T.light }}>
          Scenario controls
        </span>
        <span
          role="button"
          tabIndex={0}
          data-testid="whatif-reset"
          onClick={onReset}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onReset(); }}
          style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.blue, cursor: "pointer" }}
        >
          ↺ Reset to today
        </span>
      </div>

      {/* Primary comp levers — time horizon, net growth, days in office, sharing ratio, hotel holding */}
      <div style={{ ...rowStyle, borderTop: "none", paddingTop: 6 }}>
        <span style={nameStyle}>Time horizon</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "inline-flex", border: `1px solid ${T.border}`, borderRadius: 7, overflow: "hidden" }}>
            {WHATIF_HORIZON_OPTIONS.map(({ label, months }) => (
              <span
                key={label}
                role="button"
                tabIndex={0}
                data-testid={`whatif-horizon-${months}`}
                onClick={() => set({ horizonMonths: months })}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") set({ horizonMonths: months }); }}
                style={{
                  fontSize:   T.fsSm,
                  fontWeight: T.fwSemibold,
                  padding:    "5px 10px",
                  color:      levers.horizonMonths === months ? T.white : T.mid,
                  background: levers.horizonMonths === months ? T.dark : "transparent",
                  cursor:     "pointer",
                  userSelect: "none",
                }}
              >
                {label}
              </span>
            ))}
          </div>
          <ChevronSlotPlaceholder />
        </div>
      </div>

      <NetGrowthLever
        levers={levers}
        onChange={onChange}
        activeMarkets={activeMarkets}
        blendedNetGrowthRate={blendedNetGrowthRate}
        pinnedMarketCount={pinnedMarketCount}
      />

      <div style={rowStyle}>
        <span style={nameStyle}>Days in office / mo</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {pb && (
            <PolicyDeltaBadge
              testId="whatif-rto-policy-delta"
              delta={levers.rtoDaysPerMonth - pb.rtoDaysPerMonth}
              unit=" days"
            />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: STEPPER_CHEVRON_COL_WIDTH, flexShrink: 0, justifyContent: "flex-end" }}>
            <Stepper
              testId="whatif-rto"
              label=""
              value={levers.rtoDaysPerMonth}
              displayValue={String(levers.rtoDaysPerMonth)}
              sub={pb ? `Policy: ${pb.rtoDaysPerMonth} days/mo` : undefined}
              onChange={(v) => set({ rtoDaysPerMonth: v })}
              step={1}
              min={0}
              max={22}
            />
            <ChevronSlotPlaceholder />
          </div>
        </div>
      </div>

      <div style={rowStyle}>
        <span style={nameStyle}>Desk sharing ratio</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {pb && (
            <PolicyDeltaBadge
              testId="whatif-sharing-policy-delta"
              delta={(levers.sharingRatio ?? 1) - (pb.sharingRatio ?? 1)}
              unit=" : 1"
            />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: STEPPER_CHEVRON_COL_WIDTH, flexShrink: 0, justifyContent: "flex-end" }}>
            <Stepper
              testId="whatif-sharing"
              label=""
              value={levers.sharingRatio ?? 1}
              displayValue={<SharingRatioValue numerator={(levers.sharingRatio ?? 1).toFixed(1)} />}
              sub={pb ? <>Policy: <SharingRatioValue numerator={(pb.sharingRatio ?? 1).toFixed(1)} /></> : undefined}
              onChange={(v) => set({ sharingRatio: v })}
              step={0.1}
              min={0.5}
              max={2}
            />
            <ChevronSlotPlaceholder />
          </div>
        </div>
      </div>

      <div style={rowStyle}>
        <span style={nameStyle}>Hoteling / visitor hold</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {pb && (
            <PolicyDeltaBadge
              testId="whatif-hold-policy-delta"
              delta={levers.hotelingHoldPct - pb.hotelingHoldPct}
              unit="%"
            />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: STEPPER_CHEVRON_COL_WIDTH, flexShrink: 0, justifyContent: "flex-end" }}>
            <Stepper
              testId="whatif-hold"
              label=""
              value={levers.hotelingHoldPct}
              displayValue={`${levers.hotelingHoldPct}%`}
              sub={pb ? `Policy: ${pb.hotelingHoldPct}%` : undefined}
              onChange={(v) => set({ hotelingHoldPct: v })}
              step={1}
              min={0}
              max={40}
            />
            <ChevronSlotPlaceholder />
          </div>
        </div>
      </div>

      <div style={{ fontSize: T.fsXxs5, color: T.light, borderTop: `1px dashed ${T.lightGray}`, marginTop: 4, paddingTop: 8 }}>
        Net growth spans negative → positive; negative values render red. "Reset to today" returns every lever to
        policy baseline so Scenario = Policy (all deltas 0).
      </div>

      {/* Legacy / advanced levers — kept below the 5 primary comp levers */}
      <div style={{ ...rowStyle, marginTop: 4 }}>
        <span style={nameStyle}>Conf-room give-back</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: STEPPER_CHEVRON_COL_WIDTH, flexShrink: 0, justifyContent: "flex-end" }}>
            <Stepper
              testId="whatif-giveback"
              label=""
              value={levers.confGivebackPct}
              displayValue={`${levers.confGivebackPct}%`}
              sub={pb ? `Policy: ${pb.confGivebackPct}%` : undefined}
              onChange={(v) => set({ confGivebackPct: v })}
              step={1}
              min={0}
              max={30}
            />
            <ChevronSlotPlaceholder />
          </div>
        </div>
      </div>

      <div style={rowStyle}>
        <span style={nameStyle}>Team-block waste <span style={{ fontSize: T.fsXxs, color: T.light, fontWeight: T.fwBold, background: T.lightGray, borderRadius: 4, padding: "1px 4px", marginLeft: 3 }}>ADV</span></span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, width: STEPPER_CHEVRON_COL_WIDTH, flexShrink: 0, justifyContent: "flex-end" }}>
            <Stepper
              testId="whatif-waste"
              label=""
              value={levers.teamBlockWastePct}
              displayValue={`${levers.teamBlockWastePct}%`}
              sub={pb ? `Policy: ${pb.teamBlockWastePct}%` : undefined}
              onChange={(v) => set({ teamBlockWastePct: v })}
              step={1}
              min={0}
              max={20}
            />
            <ChevronSlotPlaceholder />
          </div>
        </div>
      </div>

      {/* Guardrail chips — values come from policy baseline when available */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap", padding: "11px 0 0" }}>
        <GuardrailChip
          label="Density floor"
          value={`${pb ? pb.densityFloorSfPerSeat : WHATIF_GUARDRAILS.densityFloorSfPerSeat} sf / seat`}
          testId="whatif-guardrail-density"
        />
        <GuardrailChip
          label="Target buffer"
          value={`${pb ? pb.targetBufferPct : WHATIF_GUARDRAILS.targetBufferPct}%`}
          testId="whatif-guardrail-buffer"
        />
        <span style={{ fontSize: T.fsXxs5, color: T.light, maxWidth: 200 }}>
          Scenario flags markets below these.{" "}
          <a
            data-testid="whatif-edit-policy-link"
            href="/admin/policy"
            style={{ color: T.blue, textDecoration: "none", fontWeight: T.fwSemibold, whiteSpace: "nowrap" }}
          >
            Edit Policy in Admin →
          </a>
        </span>
      </div>
    </div>
  );
}

// ─── Compare panel — two labeled rows per market (Today / Scenario), on a ───
// FIXED %-of-capacity scale. WHATIF-COMPARE-PCT-OF-CAP-S1: replaces the
// earlier variable-scale (BAR_SCALE_MAX) bars from WHATIF-COMPARE-REDESIGN-S1
// — every bar (Today + Scenario, every market) is now the same fixed length
// representing that market's capacity (100%); fill % = seats ÷ capacity,
// clamped so it never renders past the track end. Because the scale is fixed
// and clamped, this also inherently resolves the direction/rescaling
// concerns from the earlier WHATIF-COMPARE-BAR-DIRECTION-FIX-S1 and
// WHATIF-COMPARE-FIXED-SCALE-FIX-S1 briefs.

// Target buffer marker is a fixed position (90%) on every bar, regardless of
// the guardrail buffer %, since the fill itself is already %-of-capacity.
const TARGET_BUFFER_FIXED_PCT = 90;

/**
 * <90% Comfortable · 90–<100% Tight · ≥100% (or non-finite, i.e. capacity=0)
 * Over capacity.
 *
 * MARKET-PLAN-HEADROOM-S1: when the market has max_capacity data on file
 * (hasHeadroom), a scenario past installed capacity but within the physical
 * max-density ceiling is "densify" (teal) instead of "over" — Over capacity
 * now only fires once seats exceed usableMaxSeats. Markets with no
 * max_capacity data keep the original installed-only threshold, unchanged.
 */
function deriveFillStatus(pct, { hasHeadroom = false, seats = null, usableMaxSeats = null } = {}) {
  if (hasHeadroom) {
    const overMax = seats != null && usableMaxSeats != null && Number.isFinite(usableMaxSeats)
      ? seats > usableMaxSeats
      : !Number.isFinite(pct);
    if (overMax) return "over";
    if (!Number.isFinite(pct) || pct >= 100) return "densify";
    if (pct >= TARGET_BUFFER_FIXED_PCT) return "tight";
    return "comfortable";
  }
  if (!Number.isFinite(pct) || pct >= 100) return "over";
  if (pct >= TARGET_BUFFER_FIXED_PCT) return "tight";
  return "comfortable";
}

const STATUS_COLOR = { comfortable: T.greenDeep, tight: T.amberDark, densify: T.teal, over: T.ragRed };
const FILL_COLOR = SCENARIO_FILL_COLOR;

/**
 * Builds the readout parts for one bar: "<seats> · <P%> · <status>" for
 * Comfortable/Tight (Tight adds "· N to spare"), "<seats> · <P%> · Densify"
 * (adds "· +N desks (within max)") when within the max-density headroom, or
 * "<seats> · <status>" for Over capacity (status text folds in the %-over —
 * or the over-max seat count when headroom data is on file, or an absolute
 * seat count when capacity is 0 and no percentage can be derived).
 */
// MARKETPLAN-ROW-EXPAND-S1: status texts are intentionally short so the
// number (spare seats / desks / over count) is never clipped in the
// fixed-width readout column. Verbose labels ("Tight ·", "Densify ·",
// "over capacity") were removed; the color coding already carries the
// status meaning visually.
function buildReadout(pct, seats, usableSeats, { hasHeadroom = false, usableMaxSeats = null } = {}) {
  const status = deriveFillStatus(pct, { hasHeadroom, seats, usableMaxSeats });
  if (status === "over") {
    if (hasHeadroom && Number.isFinite(usableMaxSeats)) {
      const overSeats = Math.max(0, Math.round(seats - usableMaxSeats));
      return { status, middle: null, statusText: `${overSeats} over max` };
    }
    const overPct = Number.isFinite(pct) ? Math.max(0, Math.round(pct - 100)) : null;
    const overSeats = Math.max(0, Math.round(seats - usableSeats));
    const statusText = overPct !== null ? `${overPct}% over` : `${overSeats} over`;
    return { status, middle: null, statusText };
  }
  const roundedPct = Math.round(pct);
  if (status === "densify") {
    const desksAddable = Math.max(0, Math.round(seats - usableSeats));
    return { status, middle: `${roundedPct}%`, statusText: `+${desksAddable} desks` };
  }
  if (status === "tight") {
    const spareSeats = Math.max(0, Math.round(usableSeats - seats));
    return { status, middle: `${roundedPct}%`, statusText: `${spareSeats} to spare` };
  }
  return { status, middle: `${roundedPct}%`, statusText: "Comfortable" };
}

function CompareTrack({ testId, pct, status, hasHeadroom, seats, usableSeats, usableMaxSeats, buildSegment }) {
  const fillPct = Number.isFinite(pct) ? Math.min(100, Math.max(pct, 0)) : 100;
  // MARKET-PLAN-HEADROOM-S1: when headroom data is on file, the track's
  // right edge (100%) represents the max-density ceiling instead of
  // installed capacity — installed capacity becomes an interior marker, and
  // the gap between it and 100% renders as a light "headroom" band. Markets
  // with no max_capacity data are unchanged: the track's 100% is capacity.
  const showHeadroomBand = hasHeadroom
    && Number.isFinite(usableMaxSeats) && usableMaxSeats > 0
    && Number.isFinite(usableSeats);
  const installedMarkerPct = showHeadroomBand
    ? Math.min(100, Math.max(0, (usableSeats / usableMaxSeats) * 100))
    : 100;
  // MARKETPLAN-BAR-COLLAPSE-COINCIDENT-MARKERS: when installed capacity is
  // within ~3px of max-density (installedMarkerPct >= 97), the black marker
  // and the red max-density marker land on the same pixel and blur. Suppress
  // the black marker in this case — the red marker alone clearly communicates
  // the at-capacity state.
  const markersCoincide = showHeadroomBand && installedMarkerPct >= 97;
  // Fill width is always expressed against the same scale as the markers:
  // installed-based pct when there's no headroom, or re-based directly
  // against max-density (100% = max) using the raw seat count when headroom
  // is on file — never derived from the already-clamped installed-based
  // fillPct, or seats between installed and max would render stuck at the
  // installed marker instead of extending into the headroom band.
  const scaledFillPct = showHeadroomBand && Number.isFinite(seats)
    ? Math.min(100, Math.max(0, (seats / usableMaxSeats) * 100))
    : fillPct;
  return (
    <div data-testid={testId} style={{ position: "relative", height: 10, background: T.capacityBarTrackBg, borderRadius: 4 }}>
      {showHeadroomBand && (
        <div
          data-testid={`${testId}-headroom-band`}
          style={{ position: "absolute", top: 0, bottom: 0, left: `${installedMarkerPct}%`, right: 0, borderRadius: "0 4px 4px 0", background: T.dispRelocateBg }}
        />
      )}
      {/* WHATIF-BUILD-ONLINE-INDICATOR-S1: blue-hatched segment spanning from
          today-installed to post-build installed, representing incoming capacity
          from an in-build office. Rendered below the fill so the fill stays legible. */}
      {buildSegment && (
        <div
          data-testid={`${testId}-build-segment`}
          style={{
            position:   "absolute",
            top:        0,
            bottom:     0,
            left:       `${buildSegment.todayPct}%`,
            width:      `${Math.max(0, buildSegment.newPct - buildSegment.todayPct)}%`,
            background: `repeating-linear-gradient(45deg,${BUILD_BLUE},${BUILD_BLUE} 3px,${HATCH_WHITE} 3px,${HATCH_WHITE} 6px)`,
            opacity:    0.5,
            zIndex:     1,
          }}
        />
      )}
      {/* MARKETPLAN-COMPARE-LEGEND-CLEANUP-S1: the grey "office closing" hatch
          previously rendered here (exitSegment) has been removed as a visual
          — the exitFlagLabel text below the track still communicates the same
          closing signal, so no information is lost. exitSegment is still
          computed by the caller and used only to position that label. */}
      <div data-testid={`${testId}-fill`} style={{ position: "absolute", left: 0, top: 0, bottom: 0, borderRadius: 4, width: `${scaledFillPct}%`, background: FILL_COLOR[status], zIndex: 2 }} />
      {!markersCoincide && (
        <div
          data-testid={`${testId}-capacity-marker`}
          style={showHeadroomBand
            ? { position: "absolute", top: -2, bottom: -2, width: 2, left: `${installedMarkerPct}%`, background: SCENARIO_MARKER_COLOR.installed, zIndex: 4 }
            : { position: "absolute", top: -2, bottom: -2, width: 2, right: 0, background: SCENARIO_MARKER_COLOR.installed, zIndex: 4 }}
        />
      )}
      {showHeadroomBand && (
        <div
          data-testid={`${testId}-max-marker`}
          title="Max density"
          style={{ position: "absolute", top: -2, bottom: -2, width: 2, right: 0, background: SCENARIO_MARKER_COLOR.max, boxShadow: `0 0 0 1.5px ${T.white}`, zIndex: 6 }}
        />
      )}
      {status === "over" && (
        <span data-testid={`${testId}-overflow-tick`} style={{ position: "absolute", right: -3, top: "50%", transform: "translateY(-50%)", color: T.ragRed, fontSize: T.fsXs, fontWeight: T.fwBold, lineHeight: 1 }}>▸</span>
      )}
    </div>
  );
}

// Fixed label + flexible track + fixed readout so every row's track column
// is a true fixed-width layout slot, not a happy-accident of grid
// auto-sizing. WHATIF-COMPARE-BAR-WIDTH-LOCK-S1: the readout column used to
// be `auto` (content-sized), which meant each CompareBarRow — an
// independent grid instance — resolved its `1fr` track column against a
// different remainder depending on that row's own readout text length
// ("10% over capacity" vs "8 to spare" vs "Densify · +N desks"). That left
// the track width still effectively data-derived instead of locked. Pinning
// the readout column to a fixed pixel width guarantees every row's track
// fills the exact same remainder, so all tracks render at one identical
// width regardless of fill %, status text, or headroom data.
const READOUT_COL_WIDTH = 150;
const ROW_GRID_COLS = `58px 1fr ${READOUT_COL_WIDTH}px`;

function CompareBarRow({ rowTestId, trackTestId, readoutTestId, label, pct, seats, usableSeats, hasHeadroom = false, usableMaxSeats = null, buildSegment = null, buildFlagLabel = null, exitSegment = null, exitFlagLabel = null }) {
  const { status, middle, statusText } = buildReadout(pct, seats, usableSeats, { hasHeadroom, usableMaxSeats });
  return (
    <div data-testid={rowTestId} style={{ display: "grid", gridTemplateColumns: ROW_GRID_COLS, alignItems: "center", gap: 10, margin: "4px 0" }}>
      <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.light, textAlign: "right" }}>{label}</span>
      {/* MARKETPLAN-STICKY-HEADER-OPAQUE-FIX: isolation:isolate contains this
          row's internal z-index stack (track fill/markers/flag labels go up
          to zIndex:10) so it never compares against the sticky header's
          z-index in an ancestor stacking context — without this, a bar's
          marker could render above the sticky header regardless of the
          header's own z-index. */}
      <div style={{ position: "relative", isolation: "isolate" }}>
        <CompareTrack testId={trackTestId} pct={pct} status={status} hasHeadroom={hasHeadroom} seats={seats} usableSeats={usableSeats} usableMaxSeats={usableMaxSeats} buildSegment={buildSegment} />
        {/* WHATIF-BUILD-ONLINE-INDICATOR-S1 + WHATIF-EXIT-SEGMENT-S1: both flag labels float below the track.
            Build was previously positioned with bottom:"100%" (above), which caused it to overlap the Today row.
            Now both sit at top:"100%" (below). When both are present they stack in a flex column to avoid overlap. */}
        {(buildFlagLabel || exitFlagLabel) && (
          <div
            style={{
              position:      "absolute",
              top:           "100%",
              left:          buildFlagLabel
                               ? (buildSegment ? `${buildSegment.todayPct}%` : 0)
                               : (exitSegment  ? `${exitSegment.afterPct}%`  : 0),
              display:       "flex",
              flexDirection: "column",
              zIndex:        10,
            }}
          >
            {buildFlagLabel && (
              <div
                data-testid={`${trackTestId}-build-flag`}
                style={{
                  fontSize:   T.fsXs,
                  fontWeight: T.fwBold,
                  color:      BUILD_BLUE,
                  whiteSpace: "nowrap",
                  paddingTop: 2,
                }}
              >
                {buildFlagLabel}
              </div>
            )}
            {exitFlagLabel && (
              <div
                data-testid={`${trackTestId}-exit-flag`}
                style={{
                  fontSize:   T.fsXs,
                  fontWeight: T.fwBold,
                  color:      EXIT_GRAY,
                  whiteSpace: "nowrap",
                  paddingTop: buildFlagLabel ? 0 : 2,
                  marginTop:  buildFlagLabel ? 2 : 0,
                }}
              >
                {exitFlagLabel}
              </div>
            )}
          </div>
        )}
      </div>
      {/* MARKETPLAN-ROW-EXPAND-S1: overflow/ellipsis removed; shortened status
          text (buildReadout) guarantees the number always fits the column. */}
      <span data-testid={readoutTestId} style={{ fontSize: T.fsSm, whiteSpace: "nowrap", textAlign: "right" }}>
        {middle ? <>{middle} · </> : null}
        <span style={{ fontWeight: T.fwSemibold, color: STATUS_COLOR[status] }}>{statusText}</span>{" "}
        <span style={{ color: T.light, fontWeight: T.fwSemibold }}>{seats}</span>
      </span>
    </div>
  );
}

// ─── Inline expand pane (MARKETPLAN-ROW-EXPAND-S1) ───────────────────────────
//
// Rendered below a market's bars when the chevron is toggled open. Reads only
// from data already present on market.locations + the scenario object — no
// extra API call. Three sections:
//   1. DRIVER — one sentence explaining today's capacity state + horizon events
//   2. OFFICES table — per-office: name, status, plan, next lease event,
//      people/seats, next scheduled capacity change, headroom
//   3. EVENTS — horizon build/exit events listed in chronological order

// Column head styles are computed as a function so T.* tokens are evaluated
// at render time (tokens object is always initialised by then).
function expandColHead() {
  return {
    fontSize:      T.fsXxs,
    fontWeight:    T.fwBold,
    letterSpacing: ".07em",
    textTransform: "uppercase",
    color:         "var(--v2-mid)",
    padding:       "10px 14px",
    whiteSpace:    "nowrap",
    textAlign:     "left",
    borderBottom:  "1px solid var(--v2-line)",
    background:    "var(--v2-tint)",
  };
}
const EXPAND_COL_CELL = {
  padding:       "5px 8px 5px 0",
  verticalAlign: "middle",
};

function ExpandPane({ market, scenario }) {
  const today       = useMemo(() => new Date(), []);
  const driverText  = useMemo(() => buildMarketDriverSentence(scenario), [scenario]);
  const officeRows  = useMemo(() => buildMarketOfficeRows(market.locations, today), [market.locations, today]);

  // Merge build + exit events within the horizon, sorted chronologically
  const allEvents = useMemo(() => {
    const build = (scenario.buildEvents ?? []).map((ev) => ({
      date:  ev.onlineDate,
      text:  `+${ev.seatsAdded} seats \u2014 ${ev.officeName} online ${formatMonthYear(ev.onlineDate)}`,
      type:  "build",
    }));
    const exit = (scenario.exitEvents ?? []).map((ev) => ({
      date:  ev.closeDate,
      text:  `\u2212${ev.seatsRemoved} seats \u2014 ${ev.officeName} closes ${formatMonthYear(ev.closeDate)}`,
      type:  "exit",
    }));
    return [...build, ...exit].sort((a, b) => a.date - b.date);
  }, [scenario]);

  // MARKETPLAN-WHATIF-DECLUTTER: per-office capacity-changes list (soonest
  // first), covering both in-horizon and future (beyond-horizon) events —
  // this is the detail that used to render as inline captions under the
  // collapsed bars. Net change is scoped to events within the horizon only
  // (future events haven't taken effect yet, so they don't count toward it).
  const capacityChanges = useMemo(() => {
    const items = [];
    for (const ev of (scenario.buildEvents ?? [])) {
      items.push({ date: ev.onlineDate, future: false, type: "build", label: `${ev.officeName} +${ev.seatsAdded} online ${formatMonthYear(ev.onlineDate)}` });
    }
    for (const ev of (scenario.exitEvents ?? [])) {
      items.push({ date: ev.closeDate, future: false, type: "exit", label: `${ev.officeName} \u2212${ev.seatsRemoved} closes ${formatMonthYear(ev.closeDate)}` });
    }
    for (const ev of (scenario.futureBuildEvents ?? [])) {
      items.push({ date: ev.onlineDate, future: true, type: "build", label: `${ev.officeName} +${ev.seatsAdded} online ${formatMonthYear(ev.onlineDate)} \u2014 not yet in this horizon` });
    }
    for (const ev of (scenario.futureExitEvents ?? [])) {
      items.push({ date: ev.closeDate, future: true, type: "exit", label: `${ev.officeName} \u2212${ev.seatsRemoved} closes ${formatMonthYear(ev.closeDate)} \u2014 not yet in this horizon` });
    }
    return items.sort((a, b) => a.date - b.date);
  }, [scenario]);

  const netCapacityChange = useMemo(() => {
    const added   = (scenario.buildEvents ?? []).reduce((s, ev) => s + ev.seatsAdded, 0);
    const removed = (scenario.exitEvents ?? []).reduce((s, ev) => s + ev.seatsRemoved, 0);
    return added - removed;
  }, [scenario]);

  return (
    <div
      data-testid={`whatif-expand-pane-${market.id}`}
      style={{
        margin:     "8px 0 4px 0",
        padding:    "10px 14px",
        background: T.tint ?? T.lightGray,
        borderRadius: 8,
        border:     `1px solid ${T.border}`,
      }}
    >
      {/* 1. DRIVER */}
      <div
        data-testid={`whatif-driver-${market.id}`}
        style={{ fontSize: T.fsSm, color: T.dark, lineHeight: 1.55, marginBottom: 10 }}
      >
        <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", color: T.light }}>
          Driver
        </span>
        {"  "}
        {driverText}
      </div>

      {/* 2. OFFICES table */}
      {officeRows.length > 0 && (
        <div style={{ overflowX: "auto", marginBottom: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
            <thead>
              <tr>
                <th style={expandColHead()}>Office</th>
                <th style={expandColHead()}>Stage</th>
                <th style={expandColHead()}>Plan</th>
                <th style={{ ...expandColHead(), textAlign: "right" }}>People / Seats</th>
                <th style={{ ...expandColHead(), textAlign: "right" }}>Expansion</th>
                <th style={expandColHead()}>Next lease event</th>
                <th style={expandColHead()}>Next sched. change</th>
              </tr>
            </thead>
            <tbody>
              {officeRows.map((row) => (
                <tr key={row.id} style={{ borderBottom: `1px solid ${T.lightGray}` }}>
                  <td style={{ ...EXPAND_COL_CELL, fontWeight: T.fwSemibold, color: T.dark }}>
                    {row.name}
                  </td>
                  <td style={{ ...EXPAND_COL_CELL }}>
                    <StageBadge stage={row.stage} />
                  </td>
                  <td style={{ ...EXPAND_COL_CELL, color: T.mid }}>{row.plan}</td>
                  <td style={{ ...EXPAND_COL_CELL, textAlign: "right", color: T.dark }}>
                    {row.people !== null
                      ? <>{row.people}<span style={{ color: T.light }}>{" / "}{row.seats}</span></>
                      : <span style={{ color: T.light }}>{"— / "}{row.seats}</span>
                    }
                  </td>
                  <td style={{ ...EXPAND_COL_CELL, textAlign: "right" }}>
                    {row.headroom !== null ? (
                      <span style={{
                        color:      row.headroom > 0 ? T.greenDeep : T.ragRed,
                        fontWeight: T.fwSemibold,
                      }}>
                        {row.headroom > 0 ? "+" : ""}{row.headroom}
                      </span>
                    ) : (
                      <span style={{ color: T.light }}>—</span>
                    )}
                  </td>
                  <td style={{ ...EXPAND_COL_CELL, color: T.mid }}>
                    {row.nextEvent
                      ? <>{row.nextEvent.label}{" "}<span style={{ color: T.light }}>({row.nextEvent.months} mo)</span></>
                      : <span style={{ color: T.light }}>—</span>
                    }
                  </td>
                  <td style={{ ...EXPAND_COL_CELL, color: T.mid }}>
                    {row.nextSchedule ? (
                      <>
                        <span style={{
                          color:      row.nextSchedule.delta > 0 ? T.greenDeep : T.ragRed,
                          fontWeight: T.fwSemibold,
                        }}>
                          {row.nextSchedule.delta > 0 ? "+" : ""}{row.nextSchedule.delta}
                        </span>
                        {" seats · "}{row.nextSchedule.dateStr}
                      </>
                    ) : (
                      <span style={{ color: T.light }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 3. EVENTS */}
      {allEvents.length > 0 && (
        <div data-testid={`whatif-events-${market.id}`} style={{ fontSize: T.fsXs, color: T.mid, lineHeight: 1.6 }}>
          <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", color: T.light }}>
            Events
          </span>
          {"  "}
          {allEvents.map((ev, i) => (
            <span key={i}>
              <span style={{
                color:      ev.type === "build" ? BUILD_BLUE : EXIT_GRAY,
                fontWeight: T.fwSemibold,
              }}>
                {ev.text}
              </span>
              {i < allEvents.length - 1 ? " · " : ""}
            </span>
          ))}
        </div>
      )}

      {/* 4. CAPACITY CHANGES — the detail formerly shown as inline bar
          captions on the collapsed row (MARKETPLAN-WHATIF-DECLUTTER). */}
      {capacityChanges.length > 0 && (
        <div data-testid={`whatif-capacity-changes-${market.id}`} style={{ fontSize: T.fsXs, color: T.mid, lineHeight: 1.6, marginTop: 10 }}>
          <span style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", color: T.light }}>
            Capacity changes
          </span>
          <div style={{ marginTop: 4 }}>
            {capacityChanges.map((c, i) => (
              <div
                key={i}
                data-testid={`whatif-capacity-change-${market.id}-${i}`}
                style={{
                  color:      c.future ? T.light : (c.type === "build" ? BUILD_BLUE : EXIT_GRAY),
                  fontWeight: c.future ? T.fwSemibold : T.fwBold,
                }}
              >
                {c.label}
              </div>
            ))}
          </div>
          <div
            data-testid={`whatif-capacity-net-change-${market.id}`}
            style={{ marginTop: 4, color: T.dark, fontWeight: T.fwSemibold }}
          >
            Net change within horizon: {netCapacityChange >= 0 ? "+" : ""}{netCapacityChange} seats
          </div>
        </div>
      )}
    </div>
  );
}

function CompareRow({ market, scenario, isExpanded, onToggle }) {
  // Effective office count excludes Draft scaffolding; the expanded browse
  // table can still show it. Vacated retains its existing count semantics.
  const memberLocs = (market.locations ?? []).filter(isMarketMember);
  const officeCount = memberLocs.filter(isCountedMarketOffice).length;

  const todaySeats    = Math.round(scenario.demandToday);
  const scenarioSeats = Math.round(scenario.demandScenario);
  const hasHeadroom = !!scenario.hasHeadroomData;

  // WHATIF-BUILD-ONLINE-INDICATOR-S1: compute build segment percentages for
  // the Scenario bar. The track's 100% = usableMaxSeatsScenario (when
  // headroom is on file) or usableSeatsScenario (when no headroom). The
  // segment spans from today-installed to post-build installed.
  const buildEvents       = scenario.buildEvents ?? [];
  const futureBuildEvents = scenario.futureBuildEvents ?? [];
  const hasBuildOnline    = buildEvents.length > 0;

  let buildSegment = null;
  let buildFlagLabel = null;
  if (hasBuildOnline) {
    const trackScale = hasHeadroom ? (scenario.usableMaxSeatsScenario ?? scenario.usableSeatsScenario) : scenario.usableSeatsScenario;
    if (trackScale > 0) {
      const todayPct = Math.min(100, Math.max(0, (scenario.capacityToday    / trackScale) * 100));
      const newPct   = Math.min(100, Math.max(0, (scenario.capacityScenario / trackScale) * 100));
      buildSegment = { todayPct, newPct };
    }

    if (buildEvents.length === 1) {
      const ev = buildEvents[0];
      buildFlagLabel = `▲ +${ev.seatsAdded} ${ev.officeName} · live ${formatMonthYear(ev.onlineDate)}`;
    } else {
      const totalAdded = buildEvents.reduce((s, ev) => s + ev.seatsAdded, 0);
      buildFlagLabel = `▲ +${totalAdded} · ${buildEvents.length} builds`;
    }
  }

  // WHATIF-EXIT-SEGMENT-S1: compute exit segment percentages for the Scenario
  // bar. The grey-hatched segment spans from the post-exit to the pre-exit
  // capacity position, showing capacity that phases out within the horizon.
  const exitEvents       = scenario.exitEvents ?? [];
  const futureExitEvents = scenario.futureExitEvents ?? [];
  const hasExitWithinHorizon = exitEvents.length > 0;

  let exitSegment = null;
  let exitFlagLabel = null;
  if (hasExitWithinHorizon) {
    const trackScale = hasHeadroom ? (scenario.usableMaxSeatsScenario ?? scenario.usableSeatsScenario) : scenario.usableSeatsScenario;
    if (trackScale > 0) {
      const firstEv = exitEvents[0];
      const lastEv  = exitEvents[exitEvents.length - 1];
      const beforePct = Math.min(100, Math.max(0, (firstEv.capacityBefore / trackScale) * 100));
      const afterPct  = Math.min(100, Math.max(0, (lastEv.capacityAfter  / trackScale) * 100));
      exitSegment = { beforePct, afterPct };
    }

    if (exitEvents.length === 1) {
      const ev = exitEvents[0];
      exitFlagLabel = `▼ -${ev.seatsRemoved} ${ev.officeName} · closes ${formatMonthYear(ev.closeDate)}`;
    } else {
      const totalRemoved = exitEvents.reduce((s, ev) => s + ev.seatsRemoved, 0);
      exitFlagLabel = `▼ -${totalRemoved} · ${exitEvents.length} exits`;
    }
  }

  // MARKETPLAN-ROW-EXPAND-S1: build/exit flag labels and caption rows move
  // exclusively into the expand pane. Collapsed state shows only the bars +
  // the δ-seats figure. The visual hatch segments on the Scenario bar are
  // kept so the capacity change is still visible even when collapsed.

  const [isHovered, setIsHovered] = useState(false);

  // MARKETPLAN-COMPARE-LEGEND-CLEANUP-S1: whole row toggles expand/collapse,
  // not just the "more ▾" control. The button stops propagation so a click
  // on it doesn't double-fire the toggle (row onClick + button onClick).
  const handleRowClick = () => onToggle();
  const handleMoreClick = (e) => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <div data-testid={`whatif-row-${market.id}`} style={{ padding: "10px 0", borderBottom: `1px solid ${T.lightGray}` }}>
      {/* MARKETPLAN-COMPARE-LEGEND-CLEANUP-S1: this wrapper is the clickable
          "row" surface (header + bars + delta line) — it does not extend
          into the ExpandPane below, so interacting with the detail table
          doesn't collapse the row. */}
      <div
        onClick={handleRowClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isExpanded}
        style={{
          cursor:     "pointer",
          background: isHovered ? T.hoverBg : "transparent",
          borderRadius: 6,
        }}
      >
      {/* Market header row — chevron on the right opens the inline pane */}
      <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <span style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.dark }}>{market.name}</span>
        <span style={{ fontSize: T.fsXs5, color: T.light, marginLeft: 8 }}>
          {market.region ?? "—"} · {officeCount} office{officeCount === 1 ? "" : "s"}
        </span>
        {/* MAX-CAPACITY-BACKFILL-S1: flag markets with no max_capacity data
            on file — Densify can't be shown for them. */}
        {!hasHeadroom && (
          <span
            data-testid={`whatif-no-capacity-data-flag-${market.id}`}
            title="No max-density data on file for this market's offices - Densify can't be shown; add via office edit."
            style={{ fontSize: T.fsXs5, color: T.light, marginLeft: 8 }}
          >
            ⚠ no capacity data
          </span>
        )}
        {/* MARKETPLAN-ROW-EXPAND-S1: chevron toggle — one market open at a time */}
        <button
          type="button"
          data-testid={`whatif-expand-toggle-${market.id}`}
          aria-label={isExpanded ? `Collapse ${market.name} detail` : `Expand ${market.name} detail`}
          onClick={handleMoreClick}
          style={{
            ...OUTBOUND_LINK_STYLE,
            marginLeft:  "auto",
            padding:     "0 2px",
            lineHeight:  1,
            display:     "inline-flex",
            alignItems:  "center",
            gap:         4,
            userSelect:  "none",
          }}
        >
          <span>
            {isExpanded ? "Less" : "More"}
          </span>
          <span
            style={{
              display:     "inline-block",
              transition:  "transform 0.15s ease",
              transform:   isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▾
          </span>
        </button>
      </div>

      <CompareBarRow
        rowTestId={`whatif-today-row-${market.id}`}
        trackTestId={`whatif-today-bar-${market.id}`}
        readoutTestId={`whatif-today-readout-${market.id}`}
        label="Today"
        pct={scenario.assignPctToday}
        seats={todaySeats}
        usableSeats={scenario.usableSeatsToday}
        hasHeadroom={hasHeadroom}
        usableMaxSeats={scenario.usableMaxSeatsToday}
      />
      <CompareBarRow
        rowTestId={`whatif-scenario-row-${market.id}`}
        trackTestId={`whatif-scenario-bar-${market.id}`}
        readoutTestId={`whatif-scenario-readout-${market.id}`}
        label="Scenario"
        pct={scenario.assignPctScenario}
        seats={scenarioSeats}
        usableSeats={scenario.usableSeatsScenario}
        hasHeadroom={hasHeadroom}
        usableMaxSeats={scenario.usableMaxSeatsScenario}
        buildSegment={buildSegment}
        buildFlagLabel={buildFlagLabel}
        exitSegment={exitSegment}
        exitFlagLabel={exitFlagLabel}
      />

      {/* δ-seats always visible — the only supplementary line on the collapsed row */}
      <div style={{ display: "flex", gap: 8, marginTop: 4, paddingLeft: 68, alignItems: "center" }}>
        <span data-testid={`whatif-delta-${market.id}`} style={{ fontSize: T.fsXs, color: T.light, marginLeft: "auto" }}>
          {scenario.deltaSeats >= 0 ? "+" : ""}{scenario.deltaSeats} vs today
        </span>
      </div>
      </div>

      {/* Inline expand pane — driver sentence, offices table, events */}
      {isExpanded && <ExpandPane market={market} scenario={scenario} />}
    </div>
  );
}

// MARKETPLAN-ROW-EXPAND-S1: expandedMarketId lives here (one market open at
// a time). Clicking the same market's chevron again closes it.
function WhatIfComparePanel({ rows, horizonMonths }) {
  const [expandedMarketId, setExpandedMarketId] = useState(null);

  // Deep-link: ?expandMarket=<id> opens that market row once on mount.
  const expandOnMountRef = useRef(false);
  useEffect(() => {
    if (expandOnMountRef.current) return;
    expandOnMountRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const expandId = params.get("expandMarket");
    if (expandId) setExpandedMarketId(expandId);
  }, []);

  const handleToggle = (marketId) =>
    setExpandedMarketId((prev) => (prev === marketId ? null : marketId));

  return (
    <div data-testid="whatif-compare-panel" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12 }}>
      {/* Sticky heading — MARKETPLAN-STICKY-HEADER-OPAQUE-FIX:
          explicit opaque white background spans the full card width with no
          transparent gaps, and a hairline bottom border reads as a clean
          pinned boundary once scrolled. zIndex:5 sits above scrolling market
          rows (isolated per-row, see CompareBarRow) and below the page top bar. */}
      <div data-testid="whatif-compare-header" style={{ position: "sticky", top: "var(--places-sticky-top, 0px)", zIndex: 5, background: T.white, opacity: 1, borderRadius: "12px 12px 0 0", padding: "12px 14px", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", color: T.light }}>
            Compare · Today vs Scenario
          </span>
          <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", color: T.light }}>
            Seats vs capacity @ {horizonLabel(horizonMonths)}
          </span>
        </div>
      </div>
      {/* Scrolling market rows */}
      <div style={{ padding: "0 14px 12px" }}>
        {rows.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: T.light }}>
            No markets match the current filters.
          </div>
        ) : (
          rows.map(({ market, scenario }) => (
            <CompareRow
              key={market.id}
              market={market}
              scenario={scenario}
              isExpanded={expandedMarketId === market.id}
              onToggle={() => handleToggle(market.id)}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Result pane — verdict pill + templated (non-AI) sentence ───────────────

function verdictForPortfolio(portfolio) {
  const count = portfolio.overCapacityScenario;
  if (count === 0) return { tone: "good", label: "Balanced - 0 markets over" };
  if (count <= 2)  return { tone: "warn", label: `Tight - ${count} market${count === 1 ? "" : "s"} over` };
  return { tone: "bad", label: `Strained - ${count} markets over` };
}

function WhatIfResultPane({ levers, portfolio, blendedNetGrowthRate }) {
  const verdict = verdictForPortfolio(portfolio);
  const verdictBg = verdict.tone === "good" ? T.utilGreenText : (verdict.tone === "bad" ? T.ragRedBg : T.ragAmberBg);
  const verdictFg = verdict.tone === "good" ? T.greenDeep : (verdict.tone === "bad" ? T.ragRed : T.ragAmberText);
  const verdictBgColor = verdict.tone === "good" ? T.utilGreen ?? T.lightGray : verdictBg;

  const balance = portfolio.balanceScenario;
  const balancePhrase = balance >= 0
    ? `a ${Math.round(balance)}-seat surplus`
    : `a ${Math.round(Math.abs(balance))}-seat deficit`;

  return (
    <div data-testid="whatif-result-pane" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: 0.5, textTransform: "uppercase", color: T.light, marginBottom: 10 }}>
        Scenario result
      </div>
      <span
        data-testid="whatif-verdict-pill"
        style={{ display: "inline-flex", alignItems: "center", fontSize: T.fsXs, fontWeight: T.fwSemibold, color: verdictFg, background: verdictBgColor, borderRadius: 6, padding: "2px 10px", whiteSpace: "nowrap" }}
      >
        {verdict.label}
      </span>
      <div data-testid="whatif-result-sentence" style={{ fontSize: T.fsSm, color: T.dark, lineHeight: 1.5, marginTop: 9 }}>
        At <b>{horizonLabel(levers.horizonMonths)}</b>, <b>{blendedNetGrowthRate >= 0 ? "+" : ""}{Math.round(blendedNetGrowthRate * 10) / 10}% net growth</b> and{" "}
        <b>{levers.rtoDaysPerMonth} days in office</b> push <b>{portfolio.overCapacityScenario} market{portfolio.overCapacityScenario === 1 ? "" : "s"}</b> over
        capacity; portfolio runs {balancePhrase}.
      </div>
    </div>
  );
}

// ─── WhatIfBody — 2/3 compare panel + 1/3 controls-over-result column ────────

function WhatIfListOwner({ rows, levers, onChange, onReset, policyBaseline }) {
  const portfolio = useMemo(() => computeWhatIfPortfolioKpis(rows), [rows]);

  // WHATIF-NETGROWTH-PERMARKET-S1: derived directly from rows/levers so
  // callers (MarketPlanPage) don't need to pass anything new — the blend,
  // active-market list, and pinned count are all self-contained here.
  const markets = useMemo(() => rows.map((r) => r.market), [rows]);
  const activeMarkets = useMemo(() => markets.filter(isWhatIfActiveMarket), [markets]);
  const blendedNetGrowthRate = useMemo(
    () => computeBlendedNetGrowthRate(markets, levers),
    [markets, levers],
  );
  const pinnedMarketCount = useMemo(() => {
    const activeIds = new Set(activeMarkets.map((m) => m.id));
    return Object.keys(levers.netGrowthByMarket ?? {}).filter((id) => activeIds.has(id)).length;
  }, [levers.netGrowthByMarket, activeMarkets]);

  return (
    <div data-testid="whatif-pane" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, alignItems: "start" }}>
      <WhatIfComparePanel rows={rows} horizonMonths={levers.horizonMonths} />
      <div style={{ display: "flex", flexDirection: "column", gap: 14, position: "sticky", top: 0, alignSelf: "start", overflowY: "auto", maxHeight: "100vh" }}>
        <WhatIfLeversBar
          levers={levers}
          onChange={onChange}
          onReset={onReset}
          activeMarkets={activeMarkets}
          blendedNetGrowthRate={blendedNetGrowthRate}
          pinnedMarketCount={pinnedMarketCount}
          policyBaseline={policyBaseline}
        />
        <WhatIfResultPane levers={levers} portfolio={portfolio} blendedNetGrowthRate={blendedNetGrowthRate} />
      </div>
    </div>
  );
}

export { WHATIF_LEVERS_STORAGE_KEY };
